import ReactGA from 'react-ga4';
import { analyticsConfig, EventCategory, EventAction } from '../config/analytics';

// Custom event interface
interface AnalyticsEvent {
  category: EventCategory;
  action: EventAction;
  label?: string;
  value?: number;
  userId?: string;
  customParameters?: Record<string, any>;
}

// User properties interface
interface UserProperties {
  userId?: string;
  userRole?: 'interviewer' | 'interviewee';
  profileComplete?: boolean;
  totalBookings?: number;
  memberSince?: string;
}

class AnalyticsService {
  private initialized = false;

  /**
   * Initialize Google Analytics
   */
  initialize(): void {
    if (!analyticsConfig.enabled || this.initialized) {
      console.log('Analytics disabled or already initialized');
      return;
    }

    try {
      ReactGA.initialize(analyticsConfig.measurementId);

      this.initialized = true;
      
      if (analyticsConfig.debug) {
        console.log('Google Analytics initialized with ID:', analyticsConfig.measurementId);
      }
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
    }
  }

  /**
   * Check if user has given consent for cookies/tracking
   */
  hasConsent(): boolean {
    if (!analyticsConfig.cookieConsent.required) {
      return true;
    }

    const consent = localStorage.getItem(analyticsConfig.cookieConsent.cookieName);
    return consent === 'true';
  }

  /**
   * Grant consent for analytics tracking
   */
  grantConsent(): void {
    localStorage.setItem(analyticsConfig.cookieConsent.cookieName, 'true');
    
    // Set consent expiration
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + analyticsConfig.cookieConsent.expirationDays);
    localStorage.setItem(analyticsConfig.cookieConsent.cookieName + '_expires', expirationDate.toISOString());
    
    // Initialize analytics if not already done
    if (!this.initialized) {
      this.initialize();
    }
  }

  /**
   * Revoke consent for analytics tracking
   */
  revokeConsent(): void {
    localStorage.removeItem(analyticsConfig.cookieConsent.cookieName);
    localStorage.removeItem(analyticsConfig.cookieConsent.cookieName + '_expires');
  }

  /**
   * Track page view
   */
  trackPageView(path: string, title?: string): void {
    if (!this.canTrack()) return;

    try {
      ReactGA.send({ 
        hitType: 'pageview', 
        page: path,
        title: title || document.title
      });

      if (analyticsConfig.debug) {
        console.log('Page view tracked:', path);
      }
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  /**
   * Track custom event
   */
  trackEvent(event: AnalyticsEvent): void {
    if (!this.canTrack()) return;

    try {
      ReactGA.event({
        category: event.category,
        action: event.action,
        label: event.label,
        value: event.value,
        ...event.customParameters
      });

      if (analyticsConfig.debug) {
        console.log('Event tracked:', event);
      }
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: UserProperties): void {
    if (!this.canTrack()) return;

    try {
      // Set user ID if provided
      if (properties.userId) {
        ReactGA.set({ user_id: properties.userId });
      }

      // Set custom user properties
      const customProperties: Record<string, any> = {};
      
      if (properties.userRole) {
        customProperties.user_role = properties.userRole;
      }
      
      if (properties.profileComplete !== undefined) {
        customProperties.profile_complete = properties.profileComplete;
      }
      
      if (properties.totalBookings !== undefined) {
        customProperties.total_bookings = properties.totalBookings;
      }
      
      if (properties.memberSince) {
        customProperties.member_since = properties.memberSince;
      }

      ReactGA.set(customProperties);

      if (analyticsConfig.debug) {
        console.log('User properties set:', properties);
      }
    } catch (error) {
      console.error('Failed to set user properties:', error);
    }
  }

  /**
   * Track conversion/goal completion
   */
  trackConversion(conversionName: string, value?: number): void {
    if (!this.canTrack()) return;

    try {
      ReactGA.event({
        category: 'Conversion',
        action: conversionName,
        value: value
      });

      if (analyticsConfig.debug) {
        console.log('Conversion tracked:', conversionName, value);
      }
    } catch (error) {
      console.error('Failed to track conversion:', error);
    }
  }

  /**
   * Track timing (performance metrics)
   */
  trackTiming(category: string, variable: string, value: number, label?: string): void {
    if (!this.canTrack()) return;

    try {
      ReactGA.send({
        hitType: 'timing',
        timingCategory: category,
        timingVar: variable,
        timingValue: value,
        timingLabel: label
      });

      if (analyticsConfig.debug) {
        console.log('Timing tracked:', category, variable, value);
      }
    } catch (error) {
      console.error('Failed to track timing:', error);
    }
  }

  /**
   * Track error
   */
  trackError(error: Error | string): void {
    if (!this.canTrack()) return;

    try {
      const errorDescription = error instanceof Error ? error.message : error;
      
      ReactGA.event({
        category: 'Error',
        action: 'error_occurred',
        label: errorDescription
      });

      if (analyticsConfig.debug) {
        console.log('Error tracked:', errorDescription);
      }
    } catch (trackingError) {
      console.error('Failed to track error:', trackingError);
    }
  }

  /**
   * Check if tracking is allowed
   */
  private canTrack(): boolean {
    if (!analyticsConfig.enabled || !this.initialized) {
      return false;
    }

    if (analyticsConfig.cookieConsent.required && !this.hasConsent()) {
      return false;
    }

    return true;
  }
}

// Create singleton instance
export const analytics = new AnalyticsService();

// Convenience functions for common tracking scenarios
export const trackUserAction = (action: EventAction, label?: string, value?: number) => {
  analytics.trackEvent({
    category: 'User',
    action,
    label,
    value
  });
};

export const trackBookingAction = (action: EventAction, label?: string, value?: number) => {
  analytics.trackEvent({
    category: 'Booking',
    action,
    label,
    value
  });
};

export const trackPaymentAction = (action: EventAction, label?: string, value?: number) => {
  analytics.trackEvent({
    category: 'Payment',
    action,
    label,
    value
  });
};

export const trackNavigationAction = (action: EventAction, label?: string) => {
  analytics.trackEvent({
    category: 'Navigation',
    action,
    label
  });
};

export default analytics;
